import { jwtDecode } from 'jwt-decode';
import { api } from './api';
import type { AuthUser } from '@/types';
import { resolveAssetUrl } from '@/utils/assetUrl';

type LoginResponse = {
  accessToken: string;
  user: AuthUser;
};

type JwtPayload = {
  exp: number;
  sub: string;
};

const ACCESS_TOKEN_KEY = 'ams:accessToken';
const USER_KEY = 'ams:user';

const normalizeUser = (user: AuthUser): AuthUser => ({
  ...user,
  photo: resolveAssetUrl(user.photo ?? null),
});

function persistSession({ accessToken, user }: LoginResponse) {
  const normalizedUser = normalizeUser(user);
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
}

function readCachedUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) {
    return null;
  }
  try {
    return normalizeUser(JSON.parse(raw) as AuthUser);
  } catch (error) {
    console.warn('Unable to parse cached user, clearing session', error);
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

function isTokenValid(token: string | null): boolean {
  if (!token) {
    return false;
  }
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const nowSeconds = Date.now() / 1000;
    const isValid = decoded.exp > nowSeconds;
    if (!isValid) {
      console.warn('Token expired or invalid', { exp: decoded.exp, now: nowSeconds });
    }
    return isValid;
  } catch (error) {
    console.warn('Failed to decode JWT', error);
    return false;
  }
}

async function refreshAccessToken() {
  const { data } = await api.post<{ accessToken: string }>('/auth/refresh');
  localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
  return data.accessToken;
}

export const authService = {
  async login(email: string, password: string) {
    const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
    persistSession(data);
    return normalizeUser(data.user);
  },

  logout() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  async getCurrentUser(): Promise<AuthUser | null> {
    const cachedUser = readCachedUser();
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (cachedUser && isTokenValid(accessToken)) {
      return cachedUser;
    }

    if (!accessToken) {
      return null;
    }

    try {
      const { data } = await api.get<AuthUser>('/auth/me');
      const normalized = normalizeUser(data);
      if (accessToken) {
        localStorage.setItem(USER_KEY, JSON.stringify(normalized));
      }
      return normalized;
    } catch (error) {
      if (accessToken && !isTokenValid(accessToken)) {
        try {
          await refreshAccessToken();
          const { data } = await api.get<AuthUser>('/auth/me');
          const normalized = normalizeUser(data);
          localStorage.setItem(USER_KEY, JSON.stringify(normalized));
          return normalized;
        } catch (refreshError) {
          console.warn('Session refresh failed', refreshError);
          authService.logout();
        }
      }
      return null;
    }
  },
  async uploadAvatar(file: File): Promise<AuthUser> {
    const form = new FormData();
    form.append('avatar', file);
    const { data } = await api.post<AuthUser>('/auth/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const normalized = normalizeUser(data);
    // persist updated user
    localStorage.setItem(USER_KEY, JSON.stringify(normalized));
    return normalized;
  },
};
